<template>
    <div v-if="assistent && !loading" class="assistent-edit-view">
        <div class="assistent-edit-view__menu">
            <assistent-menu>
                <assistent-goals v-if="hasWinckBranding()" v-model="assistent" />
            </assistent-menu>
        </div>

        <div class="assistent-edit-view__container">
            <div class="assistent-edit-view__form">
                <assistent-header
                    v-show="hideHeader"
                    :name="assistent.name"
                    :description="assistent.description"
                    :is-active="assistent.active"
                    :disabled="isDisabled"
                    @save="changeAssistent"
                    @activate="activateAssistent"
                    @deactivate="deactivateAssistent"
                />

                <router-view
                    v-model="assistent"
                />
            </div>

            <template v-if="!hideSidebar">
                <div
                    v-if="showDisplaySettings"
                    class="assistent-edit-view__sidebar"
                >
                    <desktop-view-form
                        v-model="assistent"
                    />
                </div>
                <div
                    v-show="!showDisplaySettings"
                    class="assistent-edit-view__preview"
                >
                    <assistent-preview
                        :assistent="assistent"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        Assistent,
        ActivateAssistentCommand,
        DeactivateAssistentCommand,
        ChangeAssistentCommand,
    } from '@keyvue/one-api-client';

    import { ButtonState, type ButtonStateChangedEvent } from '@keyvue/one-ui';
    import { computed, onMounted, type Ref, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import AssistentHeader from '@/ai/assistent/components/AssistentHeader.vue';
    import AssistentMenu from '@/ai/assistent/components/AssistentMenu.vue';
    import AssistentGoals from '@/ai/assistent/components/AssistentGoals.vue';
    import AssistentPreview from '@/ai/assistent/components/forms/display/AssistentPreview.vue';
    import DesktopViewForm from '@/ai/assistent/components/forms/display/DesktopViewForm.vue';
    import { useRouter } from 'vue-router';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const { client } = useClient();
    const router = useRouter();
    const { notifySaving, notifySaved, notifyError } = useNotify();
    const { hasWinckBranding } = useAppBranding();
    const props = defineProps<{ assistentId: string }>();
    const loading = ref(true);
    const assistent = ref<Assistent>() as Ref<Assistent>;

    const showDisplaySettings = computed(() => {
        return router.currentRoute.value.name === 'assistent-chat-edit-display';
    });

    async function loadAssistent() {
        loading.value = true;
        assistent.value = await client.get(Assistent, props.assistentId);
        loading.value = false;
    }

    async function changeAssistent(event: ButtonStateChangedEvent) {
        const closeSavingNotification = notifySaving(undefined, 0);

        try {
            await client.dispatch(
                ChangeAssistentCommand.fromAssistent(assistent.value),
            );

            closeSavingNotification();
            notifySaved(undefined, 'De AI Agent is opgeslagen');
            event.button.setState(ButtonState.completed);

            // check if assistent is now generating
            const tempAssistent = await client.get(Assistent, props.assistentId);
            if (tempAssistent.isGenerating) {
                notifySaved(undefined, 'De AI Agent is nu aan het genereren');
                await router.push({ name: 'assistent-dashboard' });
            }
        } catch (error) {
            closeSavingNotification();
            notifyError(error);
            event.button.setState(ButtonState.content);
        }
    }

    async function activateAssistent() {
        assistent.value.active = true;
        await client.dispatch(
            ActivateAssistentCommand.fromAssistent(assistent.value),
        );
        notifySaved(undefined, 'De AI agent is geactiveerd!');
    }

    async function deactivateAssistent() {
        assistent.value.active = false;
        await client.dispatch(
            DeactivateAssistentCommand.fromAssistent(assistent.value),
        );
        notifySaved(undefined, 'AI Agent is gedeactiveerd!');
    }

    const isDisabled = computed(() => {
        /**
         * Fix for partial validation to work since this scope is not to fix the validation issues on this page
         * Not a single validation on this page other than the emailrouting currently is preventing a save
         *
         * I do not git blame the devs, just the UX behind this page
         * */
        return (
            assistent.value.features.emailEnabled
            && assistent.value.emailRouting !== null
            && (assistent.value.emailRouting.forwardEmailAddress === '' || assistent.value.emailRouting.fromName === '')
        );
    });

    const hideHeader = computed(() => {
        if (!router.currentRoute.value.name) {
            return false;
        }

        return ![
            'assistent-chat-edit-handover',
            'assistent-chat-edit-chat-connect',
            'assistent-chat-edit-chat-connect-goal',
            'assistent-chat-edit-chat-connect-system-goals',
        ].includes(router.currentRoute.value.name.toString());
    });

    const hideSidebar = computed(() => {
        if (!router.currentRoute.value.name) {
            return false;
        }

        return [
            'assistent-chat-edit-handover',
        ].includes(router.currentRoute.value.name.toString());
    });

    onMounted(() => {
        loadAssistent();
    });
</script>

<style scoped lang="scss">
.assistent-edit-view {
    width: 100%;
    height: 100%;
    display: flex;
    align-content: flex-start;
    background-color: var(--theme-colors-default--white);
    box-sizing: border-box;

    &__container {
        width: calc(100% - (max(15vw, 250px)));
        display: flex;
        flex-wrap: wrap;
        gap: size(lg);
        padding: size(lg);
        box-sizing: border-box;
        overflow-y: scroll;
        align-items: flex-start;
    }

    &__header {
        width: 100%;
        display: flex;
        align-items: flex-start;
    }

    &__menu {
        width: 15vw;
        min-width: 250px;
        box-sizing: border-box;
    }

    &__form {
        width: calc(100% - 430px);
        background-color: white;
        box-sizing: border-box;
    }

    &__preview {
        width: 400px;
        background-color: #e8e6e6;
        border: 1px solid color(gray-300);
        overflow: hidden;
        box-sizing: border-box;
        border-radius: size(sm);
    }

    &__sidebar {
        width: 400px;
        background-color: #e8e6e6;
        border: 1px solid color(gray-300);
        overflow: hidden;
        box-sizing: border-box;
        border-radius: size(sm);
    }
}
</style>
